import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import Login from '@/pages/Login';
import SetPassword from '@/pages/SetPassword';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Dashboard from '@/pages/Dashboard';
import Contacts from '@/pages/Contacts';
import Companies from '@/pages/Companies';
import CustomFields from '@/pages/CustomFields';
import Settings from '@/pages/Settings';
import Export from '@/pages/Export';
import Corbeille from '@/pages/Corbeille';
import ConnecteurAutoriser from '@/pages/ConnecteurAutoriser';
import Unsubscribe from '@/pages/Unsubscribe';
import Layout from '@/components/Layout';
import DefaultViewRedirect from '@/components/DefaultViewRedirect';
import { useAuthStore } from '@/store/authStore';
import { setLoginRedirect } from '@/services/api';
import { useIsMobile } from '@/hooks/use-mobile';
import MobileContacts from '@/mobile/MobileContacts';
import MobileCompanies from '@/mobile/MobileCompanies';
import MobileUnavailable from '@/mobile/MobileUnavailable';

function AppRoutes() {
  const { isAuthenticated, logout } = useAuthStore();
  const isMobile = useIsMobile();

  useEffect(() => {
    setLoginRedirect(() => {
      logout();
      window.location.href = '/login';
    });
  }, [logout]);

  // Cible de redirection après connexion selon le device
  const homePath = isMobile ? '/contacts' : '/dashboard';

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Routes>
        {/* Routes publiques (partagées desktop / mobile) */}
        <Route
          path="/login"
          element={!isAuthenticated ? <Login /> : <Navigate to={homePath} />}
        />
        <Route path="/set-password" element={<SetPassword />} />
        <Route
          path="/forgot-password"
          element={!isAuthenticated ? <ForgotPassword /> : <Navigate to={homePath} />}
        />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        {/* Consentement du connecteur IA : hors mise en page, sur mobile comme sur ordinateur */}
        <Route path="/connecteur/autoriser" element={<ConnecteurAutoriser />} />

        {isMobile ? (
          /* ===================== INTERFACE MOBILE ===================== */
          <>
            <Route
              path="/contacts"
              element={isAuthenticated ? <MobileContacts /> : <Navigate to="/login" />}
            />
            <Route
              path="/companies"
              element={isAuthenticated ? <MobileCompanies /> : <Navigate to="/login" />}
            />
            {/* Pages réservées au desktop → écran "indisponible" */}
            <Route
              path="/dashboard"
              element={isAuthenticated ? <MobileUnavailable /> : <Navigate to="/login" />}
            />
            <Route
              path="/custom-fields"
              element={isAuthenticated ? <MobileUnavailable /> : <Navigate to="/login" />}
            />
            <Route
              path="/export"
              element={isAuthenticated ? <MobileUnavailable /> : <Navigate to="/login" />}
            />
            <Route
              path="/settings"
              element={isAuthenticated ? <MobileUnavailable /> : <Navigate to="/login" />}
            />
            <Route
              path="/corbeille"
              element={isAuthenticated ? <MobileUnavailable /> : <Navigate to="/login" />}
            />
            <Route path="/" element={<Navigate to={isAuthenticated ? '/contacts' : '/login'} />} />
            <Route path="*" element={<Navigate to={isAuthenticated ? '/contacts' : '/login'} />} />
          </>
        ) : (
          /* ===================== INTERFACE DESKTOP ===================== */
          <>
            <Route
              path="/dashboard"
              element={isAuthenticated ? <Layout><Dashboard /></Layout> : <Navigate to="/login" />}
            />
            <Route
              path="/contacts"
              element={isAuthenticated ? <Layout><Contacts /></Layout> : <Navigate to="/login" />}
            />
            <Route
              path="/companies"
              element={isAuthenticated ? <Layout><Companies /></Layout> : <Navigate to="/login" />}
            />
            <Route
              path="/custom-fields"
              element={isAuthenticated ? <Layout><CustomFields /></Layout> : <Navigate to="/login" />}
            />
            <Route
              path="/export"
              element={isAuthenticated ? <Layout><Export /></Layout> : <Navigate to="/login" />}
            />
            <Route
              path="/settings"
              element={isAuthenticated ? <Layout><Settings /></Layout> : <Navigate to="/login" />}
            />
            <Route
              path="/corbeille"
              element={isAuthenticated ? <Layout><Corbeille /></Layout> : <Navigate to="/login" />}
            />
            <Route path="/" element={<DefaultViewRedirect isAuthenticated={isAuthenticated} />} />
          </>
        )}
      </Routes>
      <Toaster />
    </div>
  );
}

export default function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppRoutes />
    </Router>
  );
}
